import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Box } from '@mui/material';
import { ScheduleV0 } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';
import { weekDays } from '@provider-portal/common/components/EditOpeningTimes/constants';
import {
  ScheduleDay,
  ScheduleDayProps,
} from '@provider-portal/common/components/EditOpeningTimes/ScheduleDay';
import { ScheduleWeek } from '@provider-portal/common/components/EditOpeningTimes/types';
import {
  convertScheduleIn,
  convertScheduleOut,
  isScheduleValid,
  normalizeSchedule,
  sortSchedule,
} from '@provider-portal/common/components/EditOpeningTimes/utils';
import { useMount } from '@provider-portal/common/hooks/useMount';
import type { Nullable } from '@provider-portal/common/types/types';
import RequestStateView from '@provider-portal/menu-common/components/RequestStateView/RequestStateView';
import OfflinePeriods from 'widgets/OpeningTimes/OfflinePeriods/OfflinePeriods';
import { SchedulePreviewWithActions } from 'widgets/OpeningTimes/SchedulePreviewWithActions';

import { NetworkRequestState } from '@bolteu/redux-session';

import { selectSelectedProvider } from 'rdx/account';
import {
  getScheduleAsyncAction,
  selectSchedule,
  selectScheduleRequestState,
  selectScheduleUpdateError,
  selectScheduleUpdateRequestState,
  updateScheduleAsyncAction,
} from 'rdx/schedule';

interface OpeningTimesProps extends ReturnType<typeof useProps> {}

const OpeningTimes: React.FC<OpeningTimesProps> = memo(
  ({ requestState, updateState, updateError, schedule, actions }) => {
    const [localSchedule, setLocalSchedule] = useState<ScheduleWeek>([]);
    const [normalizedSchedule, setNormalizedSchedule] = useState<ScheduleWeek>([]);
    const [errorIntlProps, setErrorIntlProps] = useState<{ [key: string]: string } | undefined>(
      undefined,
    );
    const [shouldShowInvalidScheduleError, setShowInvalidScheduleError] = useState<boolean>(false);
    const { formatMessage } = useIntl();

    const strings = useMemo(
      () => ({
        scheduleDay: {
          fromLabel: formatMessage({ id: 'widgets.OpeningTimes.fromLabel' }),
          toLabel: formatMessage({ id: 'widgets.OpeningTimes.toLabel' }),
          nextDay: formatMessage({ id: 'widgets.OpeningTimes.nextDay' }),
        },
        weekDays: [
          formatMessage({ id: 'widgets.OpeningTimes.monday' }),
          formatMessage({ id: 'widgets.OpeningTimes.tuesday' }),
          formatMessage({ id: 'widgets.OpeningTimes.wednesday' }),
          formatMessage({ id: 'widgets.OpeningTimes.thursday' }),
          formatMessage({ id: 'widgets.OpeningTimes.friday' }),
          formatMessage({ id: 'widgets.OpeningTimes.saturday' }),
          formatMessage({ id: 'widgets.OpeningTimes.sunday' }),
        ],
      }),
      [formatMessage],
    );

    const onChange = useCallback<ScheduleDayProps['onChange']>((index, value) => {
      setShowInvalidScheduleError(false);
      setLocalSchedule((state) => {
        const newState = state.slice();
        newState.splice(index, 1, value);
        return sortSchedule(newState);
      });
    }, []);

    const onCancel = useCallback(() => {
      setLocalSchedule(sortSchedule(convertScheduleIn(schedule as ScheduleV0)));
      setShowInvalidScheduleError(false);
    }, [schedule]);

    const onSave = useCallback(() => {
      if (isScheduleValid(normalizedSchedule)) {
        actions.saveSchedule(convertScheduleOut(localSchedule));
      } else {
        setShowInvalidScheduleError(true);
      }
    }, [actions, localSchedule, normalizedSchedule]);

    const errorMessage = useMemo(() => {
      if (updateState === NetworkRequestState.failure) {
        if (updateError) {
          setErrorIntlProps({ error: updateError });
        }
        return 'widgets.OpeningTimes.errorUpdate';
      }
      return 'widgets.OpeningTimes.errorRetrieve';
    }, [updateError, updateState]);

    useEffect(() => {
      setLocalSchedule(sortSchedule(convertScheduleIn(schedule as ScheduleV0)));
    }, [schedule]);

    useEffect(() => {
      setNormalizedSchedule(sortSchedule(normalizeSchedule(localSchedule)));
    }, [localSchedule]);

    useMount(() => {
      actions.getSchedule();
    });
    return (
      <Box>
        <RequestStateView
          isEmpty={false}
          requestState={updateState === NetworkRequestState.unset ? requestState : updateState}
          intlIdEmpty="widgets.OpeningTimes.noData"
          intlIdError={errorMessage}
          intlErrorParams={errorIntlProps}
        >
          {shouldShowInvalidScheduleError ? (
            <Box my={4}>
              <Alert variant="filled" severity="error">
                {formatMessage({ id: 'widgets.OpeningTimes.invalidSchedule' })}
              </Alert>
            </Box>
          ) : null}
          <Box display="flex">
            <Box ml={2} sx={{ minWidth: '300px' }}>
              {schedule &&
                weekDays.map((index) => (
                  <Box key={index} mb={2}>
                    <ScheduleDay
                      strings={strings.scheduleDay}
                      key={index}
                      index={index}
                      title={strings.weekDays[index]}
                      scheduleDay={localSchedule[index] || []}
                      onChange={onChange}
                    />
                  </Box>
                ))}
            </Box>
            <Box ml={2} sx={{ minWidth: '300px' }}>
              <SchedulePreviewWithActions
                weekDayStrings={strings.weekDays}
                onSave={onSave}
                schedule={normalizedSchedule}
                onCancel={onCancel}
              />
            </Box>
            <Box ml={2} sx={{ minWidth: '300px' }}>
              <OfflinePeriods />
            </Box>
          </Box>
        </RequestStateView>
      </Box>
    );
  },
);

function useActions(providerId: Nullable<number>) {
  const dispatch = useDispatch();
  return useMemo(
    () => ({
      saveSchedule: (schedule: ScheduleV0) =>
        dispatch(updateScheduleAsyncAction.request({ providerId, schedule })),
      getSchedule: () => dispatch(getScheduleAsyncAction.request()),
    }),
    [providerId, dispatch],
  );
}

function useProps() {
  const requestState = useSelector(selectScheduleRequestState);
  const updateState = useSelector(selectScheduleUpdateRequestState);
  const updateError = useSelector(selectScheduleUpdateError);
  const schedule = useSelector(selectSchedule) || [];
  const providerId = useSelector(selectSelectedProvider)?.providerId || null;
  const countryCode = useSelector(selectSelectedProvider)?.countryCode || null;

  return {
    updateState,
    updateError,
    requestState,
    schedule,
    countryCode,
    actions: useActions(providerId),
  };
}

export default () => <OpeningTimes {...useProps()} />;
